/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import {
  mapBy,
  uniq,
  without,
} from '@linc-technologies/linced-common/utils/arrays';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import {
  AutoGroupTypes,
  AttendanceParams,
  CalculatedParams,
  JurisdictionalParams,
} from '@linc-technologies/linced-common/enums/group-auto-groups';
import moment from 'moment';
import config from 'linced-app/config/environment';

export default class AutogroupsController extends Controller {
  // Services
  @service
  fetch;

  @service
  paperToaster;

  @service
  router;

  @service
  session;

  @service
  store;

  // Attrs
  AutoGroupTypes = AutoGroupTypes;

  JurisdictionalParams = JurisdictionalParams;
  CalculatedParams = CalculatedParams;
  AttendanceParams = AttendanceParams;

  // Props
  @tracked
  group = null;

  // State
  @tracked
  groupDialogOpen = false;

  // isRecalculating specifies the current state of auto group recalculation.
  @tracked
  isRecalculating = false;

  @tracked
  saveInProgress = false;
  @tracked
  deleteGroupDialogOpen = false;
  @tracked
  deleteInProgress = false;
  @tracked
  errorMessage = '';

  @computed('model.@each.grouping')
  get groupings() {
    return uniq(
      mapBy(
        this.model
          .slice()
          .sort((a, b) => (a.grouping || '').localeCompare(b.grouping || '')),
        'grouping',
      ),
    );
  }

  @action
  toggleGroupDialog() {
    this.errorMessage = '';
    this.toggleProperty('groupDialogOpen');
    if (!this.groupDialogOpen) {
      this.resetForm();
    }
  }

  @action
  newGroup() {
    this.group = this.store.createRecord('group', {
      type: 6, // GroupType.AUTO
      schoolId: this.session.school,
      year: moment().format('YYYY'),
    });
    this.group.set('params', this.store.createFragment('autogroup-params'));
    this.groupDialogOpen = true;
  }

  @action
  editGroup(group) {
    this.store.findRecord('group', group.id).then((record) => {
      this.group = record;
      this.groupDialogOpen = true;
    });
  }

  @action
  setAutoGroupType(type) {
    this.group.set('autoGroupType', type.value);
    // Reset params
    this.group.set('params', this.store.createFragment('autogroup-params'));
    if (type.value === 2) {
      // Jurisdictional - set jurisdiction to NZ | TODO: change this when we expand to other jurisdictions
      this.group.set('params.jurisdiction', 'NZ');
    }
  }

  @action
  setTypeParam(type) {
    this.group.set('params.type', type.value);
  }

  @action
  addFieldValue(fieldValue) {
    if (isEmpty(this.group.get('params.values'))) {
      this.group.set('params.values', []);
    }
    this.group.params.values.push(fieldValue);
  }

  @action
  removeFieldValue(fieldValue) {
    // params.values is a plain array, so reassign rather than mutate in place.
    let values = this.group.get('params.values') ?? [];
    this.group.set('params.values', without(values, fieldValue));
  }

  @action
  saveGroup() {
    this.saveInProgress = true;
    this.errorMessage = '';
    let action = 'create';

    if (!this.group.get('isNew')) {
      action = 'update';
    }

    this.group
      .save()
      .then(() => {
        this.groupDialogOpen = false;
        if (action === 'create') {
          // Send an event to trigger the route to refresh the model
          this.router.refresh('admin.autogroups');
        }
        this.resetForm();
      })
      .catch((err) => {
        this.handleError(err, action);
      })
      .then(() => {
        this.saveInProgress = false;
      });
  }

  @action
  deleteGroup(group) {
    this.errorMessage = '';
    this.store.findRecord('group', group.id).then((record) => {
      this.group = record;
    });
    this.deleteGroupDialogOpen = true;
  }

  @action
  confirmDelete(confirmed) {
    return new Promise((resolve, reject) => {
      if (confirmed) {
        // Deletion confirmed; actually delete the record.
        this.deleteInProgress = true;

        this.group
          .destroyRecord()
          .then(resolve)
          .catch((err) => {
            // Remove 'deleted' flag so that entity can be interacted with again.
            this.group.rollbackAttributes();
            reject(err);
          });
      } else {
        resolve();
      }
    })
      .then(() => {
        // Tidy up / close dialog.
        this.deleteGroupDialogOpen = false;
        this.resetForm();
      })
      .catch((err) => {
        this.handleError(err, 'delete');
      })
      .then(() => {
        this.deleteInProgress = false;
      });
  }

  @action
  recalculateAutogroups() {
    let uri = `${config.APP.apiHost}/groups/${config.APP.apiPath}/groups/:recalculate`;
    let schoolId = this.session.activeSchool;
    let data = {
      schoolId: schoolId,
    };
    let options = {
      data: JSON.stringify(data),
    };

    this.isRecalculating = true;
    this.fetch
      .post(uri, options)
      .then(() => {
        let query = {
          schoolId,
          year: moment().format('YYYY'),
        };
        this.store.query('group', query);
      })
      .catch(() => {
        this.paperToaster.show(`Error recalculating auto group membership!`, {
          duration: 4000,
          position: 'top right',
        });
      })
      .finally(() => {
        this.isRecalculating = false;
      });
  }

  handleError(err) {
    if (!isEmpty(err.errors.msg)) {
      this.errorMessage = err.errors.msg;
    } else if (!isEmpty(err.errors[0])) {
      let error = err.errors[0];
      if (!isEmpty(error.detail)) {
        this.errorMessage = error.detail;
      }
    } else {
      this.errorMessage = err.message;
    }
  }

  resetForm() {
    if (this.group.isNew) {
      this.group.deleteRecord();
    }
    this.group = null;
  }
}
